import { useState, useEffect, useRef, useCallback } from "react";
import styles from "./ImageSlider.module.css";
import AngleLeftIcon from "../Icons/AngleLeftIcon";
import AngleRightIcon from "../Icons/AngleRightIcon";
import Tooltip from "../Tooltip/Tooltip";

const MaxDotsPerPage = 15;

export default function ImageSlider({
    items = [],
    showDots = true,
    showArrows = true,
    onItemClick,
    activeKey,
    renderItem,
}: {
    items: any[];
    showDots?: boolean;
    showArrows?: boolean;
    onItemClick?: (item: any) => void;
    activeKey?: any;
    renderItem: (item: any, index: number, isActive: string) => React.ReactNode;
}) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [itemsPerView, setItemsPerView] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);
    const swipeRef = useRef<HTMLDivElement>(null);

    const startX = useRef(0);
    const currentX = useRef(0);
    const isSwiping = useRef(false);
    const isTap = useRef(true);

    const len = items.length;

    const [dotPage, setDotPage] = useState(0);
    const totalDots = Math.ceil(len / itemsPerView);
    const currentDotIndex = Math.floor(currentIndex / itemsPerView);
    const currentDotPage = Math.floor(currentDotIndex / MaxDotsPerPage);
    const startDot = dotPage * MaxDotsPerPage;
    const endDot = Math.min(startDot + MaxDotsPerPage, totalDots);

    useEffect(() => {
        if (dotPage !== currentDotPage) setDotPage(currentDotPage);
    }, [currentDotPage, dotPage]);

    useEffect(() => {
        let timeoutId: any;

        const updateItemsPerView = () => {
            clearTimeout(timeoutId);

            timeoutId = setTimeout(() => {
                setItemsPerView(1);

                requestAnimationFrame(() => {
                    if (containerRef.current) {
                        const containerWidth = containerRef.current.offsetWidth - 40;
                        const imageWidth = 50;
                        const count = Math.max(1, Math.floor(containerWidth / imageWidth));
                        setItemsPerView(count);
                    }
                });
            }, 100);
        };

        updateItemsPerView();
        window.addEventListener("resize", updateItemsPerView);

        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener("resize", updateItemsPerView);
        };
    }, []);

    const nextSlide = useCallback(() => {
        setCurrentIndex((prev) => {
            const next = prev + itemsPerView;
            return next >= len ? 0 : next;
        });
    }, [itemsPerView, len]);

    const prevSlide = useCallback(() => {
        setCurrentIndex((prev) => {
            const next = prev - itemsPerView;
            return next < 0 ? Math.max(len - itemsPerView, 0) : next;
        });
    }, [itemsPerView, len]);

    useEffect(() => {
        const el = swipeRef.current;
        if (!el) return;

        const SWIPE_THRESHOLD = 40;
        const TAP_THRESHOLD = 10;

        const onTouchStart = (e: TouchEvent) => {
            const touch = e.touches[0];
            startX.current = touch.clientX;
            currentX.current = touch.clientX;
            isSwiping.current = false;
            isTap.current = true;
        };

        const onTouchMove = (e: TouchEvent) => {
            const touch = e.touches[0];
            currentX.current = touch.clientX;

            if (Math.abs(currentX.current - startX.current) > TAP_THRESHOLD) {
                isTap.current = false;
            }
        };

        const onTouchEnd = () => {
            const delta = currentX.current - startX.current;

            if (Math.abs(delta) > SWIPE_THRESHOLD) {
                isSwiping.current = true;

                if (delta > 0) prevSlide();
                else nextSlide();
            }

            setTimeout(() => {
                isSwiping.current = false;
            }, 50);
        };

        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el.addEventListener("touchmove", onTouchMove, { passive: true });
        el.addEventListener("touchend", onTouchEnd);

        return () => {
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
        };
    }, [prevSlide, nextSlide]);

    return (
        <div className={styles.slider} ref={containerRef}>
            <div className={styles.flexRowContainer}>
                {/* Left Arrow */}
                {showArrows && len > 1 && (
                    <span onClick={prevSlide} className={styles.arrowBtn}>
                        <AngleLeftIcon size={18} color="#333" />
                    </span>
                )}

                {/* Image Container */}
                <div className={styles.slideContainer} ref={swipeRef}>
                    {Array.from({ length: Math.min(itemsPerView, len) }).map((_, i) => {
                        const itemIndex = (currentIndex + i) % len;
                        const item = items[itemIndex];
                        return (
                            <div
                                key={i}
                                className={styles.slideItem}
                                onClick={(e) => {
                                    if (isSwiping.current || !isTap.current) {
                                        e.preventDefault();
                                        return;
                                    }
                                    onItemClick?.(item);
                                }}
                            >
                                {renderItem(item, itemIndex, activeKey)}
                            </div>
                        );
                    })}
                </div>

                {/* Right Arrow */}
                {showArrows && len > 1 && (
                    <span onClick={nextSlide} className={styles.arrowBtn}>
                        <AngleRightIcon size={18} color="#333" />
                    </span>
                )}
            </div>

            {/* Dots */}
            {showDots && len > 1 && (
                <div className={styles.dotsWrapper}>
                    {Array.from({ length: totalDots })
                        .slice(startDot, endDot)
                        .map((_, idx) => {
                            const actualIndex = startDot + idx;
                            return (
                                <span
                                    key={actualIndex}
                                    className={`${styles.dot} ${
                                        actualIndex === currentDotIndex ? styles.activeDot : ""
                                    }`}
                                    onClick={() => setCurrentIndex(actualIndex * itemsPerView)}
                                />
                            );
                        })}
                </div>
            )}
        </div>
    );
}
